/**
 * MenuExplorer — restaurant-style browser with compact cards + full-detail modal.
 *
 * Card = image + name + station + dietary badges (NO description).
 * Tap = smooth modal with hero image + full description.
 * Search + station chips + dietary filter chips all combine live.
 */
import { useEffect, useMemo, useState } from "react";
import { Search, X, MapPin } from "lucide-react";
import DetailModal from "./DetailModal";
import VenueMapModal from "./VenueMapModal";
import { mapBehaviorFor, hasMapBehavior } from "../data/venueMapConfig";
import {
  CATEGORIES,
  STATION_ORDER,
  FILTER_CHIPS,
  TAG_DEFS,
  enrichDish,
} from "../data/menuData";
import { T } from "../lib/i18n";

export default function MenuExplorer({ menu }) {
  const [category, setCategory] = useState("all");
  const [station, setStation] = useState(null);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState([]);
  const [selected, setSelected] = useState(null); // dish object or null
  const [mapOpenFor, setMapOpenFor] = useState(null); // dish whose map is open

  const enriched = useMemo(() => (menu || []).map(enrichDish), [menu]);

  const inCategory = useMemo(
    () => category === "all" ? enriched : enriched.filter((d) => d.categoryId === category),
    [enriched, category]
  );

  const stations = useMemo(() => {
    if (category === "all") {
      // Aggregate all stations from every category (canonical order)
      const all = [].concat(...Object.values(STATION_ORDER));
      const present = new Set(inCategory.map((d) => d.stationLabel));
      // Preserve unique order
      const seen = new Set();
      return all.filter((s) => present.has(s) && !seen.has(s) && seen.add(s));
    }
    const ordered = STATION_ORDER[category] || [];
    const present = new Set(inCategory.map((d) => d.stationLabel));
    return ordered.filter((s) => present.has(s));
  }, [inCategory, category]);

  const filtered = useMemo(() => {
    let list = inCategory;
    if (station) list = list.filter((d) => d.stationLabel === station);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          (d.description || "").toLowerCase().includes(q) ||
          (d.stationLabel || "").toLowerCase().includes(q) ||
          (d.section || "").toLowerCase().includes(q) ||
          d.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (filters.length > 0) {
      list = list.filter((d) => filters.every((f) => d.tags.includes(f)));
    }
    return list;
  }, [inCategory, station, query, filters]);

  const toggleFilter = (id) =>
    setFilters((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const clearAll = () => {
    setStation(null);
    setQuery("");
    setFilters([]);
  };

  if (!menu || menu.length === 0) {
    return <div className="text-motif-muted text-sm py-4 text-center">{T("Menu is being prepared…")}</div>;
  }

  return (
    <div className="space-y-3" data-testid="menu-explorer">
      <div className="flex items-center gap-2">
        <div className="font-display text-xl text-motif">{T("Menu Explorer")}</div>
        {(station || query || filters.length > 0) && (
          <button onClick={clearAll} className="btn-ghost text-[10px] tracking-luxe ml-auto flex items-center gap-1" data-testid="menu-clear">
            <X className="w-3 h-3" /> {T("Clear")}
          </button>
        )}
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 flex-wrap justify-center">
        <button
          onClick={() => { setCategory("all"); setStation(null); }}
          className="px-3 py-2 rounded-full text-xs tracking-wide flex items-center gap-1.5 transition"
          style={{
            background: category === "all" ? "var(--motif-primary)" : "var(--motif-accent)",
            color: category === "all" ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
          }}
          data-testid="menu-cat-all"
        >
          All
        </button>
        {CATEGORIES.map((c) => {
          const active = c.id === category;
          return (
            <button
              key={c.id}
              onClick={() => { setCategory(c.id); setStation(null); }}
              className="px-3 py-2 rounded-full text-xs tracking-wide flex items-center gap-1.5 transition"
              style={{
                background: active ? "var(--motif-primary)" : "var(--motif-accent)",
                color: active ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
              }}
              data-testid={`menu-cat-${c.id}`}
            >
              <span>{c.emoji}</span>
              {T(c.label)}
            </button>
          );
        })}
      </div>

      {/* Station chips — wrap, centered, no horizontal scroll */}
      {stations.length >= 1 && (
        <div className="flex gap-1.5 flex-wrap justify-center">
          <button
            onClick={() => setStation(null)}
            className="px-3 py-1 rounded-full text-[11px] tracking-wide transition"
            style={{
              background: station === null ? "var(--motif-primary)" : "transparent",
              color: station === null ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
              border: `1px solid ${station === null ? "var(--motif-primary)" : "var(--motif-border)"}`,
            }}
            data-testid="menu-station-all"
          >
            {T("All Stations")}
          </button>
          {stations.map((s) => {
            const active = s === station;
            return (
              <button
                key={s}
                onClick={() => setStation(active ? null : s)}
                className="px-3 py-1 rounded-full text-[11px] tracking-wide transition"
                style={{
                  background: active ? "var(--motif-primary)" : "transparent",
                  color: active ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
                  border: `1px solid ${active ? "var(--motif-primary)" : "var(--motif-border)"}`,
                }}
                data-testid={`menu-station-${s.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s}
              </button>
            );
          })}
        </div>
      )}

      {/* Dietary filter chips — wrap, centered */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-motif-muted pointer-events-none" />
          <input
            className="input-motif"
            style={{ paddingLeft: "2.25rem" }}
            placeholder={T("Search dishes…")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="menu-search"
            aria-label="Search dishes"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap justify-center">
          {FILTER_CHIPS.map((f) => {
            const active = filters.includes(f.id);
            const tone = TAG_DEFS[f.id]?.tone || "var(--motif-primary)";
            return (
              <button
                key={f.id}
                onClick={() => toggleFilter(f.id)}
                className="px-2.5 py-1 rounded-full text-[10px] tracking-wide transition"
                style={{
                  background: active ? tone : "transparent",
                  color: active ? "#fff" : tone,
                  border: `1px solid ${tone}${active ? "" : "60"}`,
                }}
                data-testid={`menu-filter-${f.id}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results */}
      <div className="text-[10px] tracking-luxe text-motif-muted pt-1" data-testid="menu-results-count">
        {filtered.length} {T(filtered.length === 1 ? "dish" : "dishes")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {filtered.map((d) => (
          <DishCard key={d.id} d={d} onOpen={() => setSelected(d)} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-motif-muted text-sm text-center py-8 italic">
            {T("No dishes match — try adjusting search or filters.")}
          </div>
        )}
      </div>

      {selected && (
        <DetailModal
          onClose={() => setSelected(null)}
          image={selected.hero || selected.image}
          imageAlt={selected.name}
          imageFit={selected.imgMeta?.fit || "contain"}
          imageBg="var(--motif-accent)"
          fallback={{ grad: selected.grad, emoji: selected.emoji }}
          eyebrow={selected.stationLabel}
          title={selected.name}
          badges={(selected.tags || [])
            .map((t) => (TAG_DEFS[t] ? { key: t, label: TAG_DEFS[t].full, tone: TAG_DEFS[t].tone } : null))
            .filter(Boolean)}
          description={selected.description}
          action={
            hasMapBehavior(selected) && mapBehaviorFor(selected)
              ? { label: T("View on Map"), onClick: () => setMapOpenFor(selected) }
              : null
          }
          testIdPrefix="menu-modal"
        />
      )}

      {mapOpenFor && (
        <VenueMapModal
          onClose={() => setMapOpenFor(null)}
          behavior={mapBehaviorFor(mapOpenFor)}
          itemName={mapOpenFor.name}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Card: identical height (72px), image + name + station + dietary badges only
// -------------------------------------------------------------------------
function DishCard({ d, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="card-elev p-2.5 flex items-center gap-3 text-left fadein transition hover:shadow-md active:opacity-80 hover:-translate-y-[1px]"
      style={{ minHeight: 88 }}
      data-testid={`menu-card-${d.id}`}
      aria-label={`Open details for ${d.name}`}
    >
      <DishThumb dish={d} />
      <div className="flex-1 min-w-0">
        <div className="font-display text-[15px] text-motif leading-tight truncate">{d.name}</div>
        {d.stationLabel && (
          <div className="text-[10px] text-motif-muted flex items-center gap-0.5 mt-0.5 truncate">
            <MapPin className="w-2.5 h-2.5 text-primary-motif flex-shrink-0" />
            {d.stationLabel}
          </div>
        )}
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {(d.tags || []).slice(0, 3).map((t) => {
            const b = TAG_DEFS[t];
            if (!b) return null;
            return (
              <span
                key={t}
                className="text-[9px] px-1.5 py-0.5 rounded-full font-medium leading-none"
                style={{ background: `${b.tone}18`, color: b.tone, border: `1px solid ${b.tone}55` }}
                title={b.full}
              >
                {b.label}
              </span>
            );
          })}
        </div>
      </div>
    </button>
  );
}

// Consistent 64×64 rounded thumbnail. Cards use the 320px /thumb/ asset
// (crisp at 2–3× Retina density without shipping the 800px hero); falls back
// to the full image, then the emoji tile.
function DishThumb({ dish, size = 64, className = "" }) {
  const [stage, setStage] = useState(0); // 0=v2 srcset, 1=legacy image, 2=emoji
  const meta = dish.imgMeta;
  const style = { width: size, height: size };
  if (stage === 0 && meta) {
    return (
      <img
        src={meta.jpg}
        srcSet={`${meta.t} 400w, ${meta.c} 800w`}
        sizes={`${size}px`}
        alt={dish.name}
        loading="lazy"
        decoding="async"
        onError={() => setStage(1)}
        className={`rounded-xl flex-shrink-0 shadow-sm object-cover ${className}`}
        style={{ ...style, objectFit: "cover" }}
      />
    );
  }
  const src = stage <= 1 ? (dish.thumb || dish.image) : null;
  if (src) {
    return (
      <img
        src={src}
        alt={dish.name}
        loading="lazy"
        decoding="async"
        onError={() => setStage(2)}
        className={`rounded-xl flex-shrink-0 shadow-sm object-cover ${className}`}
        style={{ ...style, objectFit: "cover" }}
      />
    );
  }
  return (
    <div
      className={`rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${className}`}
      style={{ ...style, background: `linear-gradient(135deg, ${dish.grad[0]}, ${dish.grad[1]})` }}
      aria-label={dish.name}
    >
      <span className="text-2xl" role="img" aria-hidden="true">{dish.emoji}</span>
    </div>
  );
}
